import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, ExternalLink, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ImageEditor from './ImageEditor';

// Accepts either:
//   - { src, alt, onClose }                      single image
//   - { images: [{src, alt}], startIndex, onClose }  gallery with nav
//
// Optional `onSendEdited(blob, fileName)` enables an "Editar" button that opens
// the annotation editor; the caller is responsible for uploading + sending the
// resulting blob in whichever conversation/ticket the lightbox was opened from.
export default function ImageLightbox({ src, alt, images, startIndex = 0, onClose, onSendEdited }) {
  const { t } = useTranslation();
  const list = images && images.length ? images : (src ? [{ src, alt }] : []);
  const [index, setIndex] = useState(Math.min(Math.max(startIndex, 0), Math.max(list.length - 1, 0)));
  const [zoomed, setZoomed] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [editing, setEditing] = useState(false);
  const dragState = useRef(null);

  const current = list[index];
  const hasMany = list.length > 1;

  const goPrev = useCallback(() => {
    setZoomed(false); setPan({ x: 0, y: 0 });
    setIndex(i => (i - 1 + list.length) % list.length);
  }, [list.length]);

  const goNext = useCallback(() => {
    setZoomed(false); setPan({ x: 0, y: 0 });
    setIndex(i => (i + 1) % list.length);
  }, [list.length]);

  useEffect(() => {
    if (!current) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && hasMany) goPrev();
      else if (e.key === 'ArrowRight' && hasMany) goNext();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [current, hasMany, onClose, goPrev, goNext]);

  if (!current) return null;

  const stop = (e) => e.stopPropagation();

  const toggleZoom = (e) => {
    e.stopPropagation();
    setZoomed(z => !z);
    setPan({ x: 0, y: 0 });
  };

  const onPointerDown = (e) => {
    if (!zoomed) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragState.current = { startX: e.clientX - pan.x, startY: e.clientY - pan.y };
  };
  const onPointerMove = (e) => {
    if (!zoomed || !dragState.current) return;
    setPan({ x: e.clientX - dragState.current.startX, y: e.clientY - dragState.current.startY });
  };
  const onPointerUp = () => { dragState.current = null; };

  const iconBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    background: 'rgba(0,0,0,0.55)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 8,
    color: '#fff',
    cursor: 'pointer',
    padding: 0,
  };

  const actionBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    background: 'rgba(0,0,0,0.6)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 8,
    color: '#fff',
    fontSize: 12,
    fontWeight: 500,
    textDecoration: 'none',
    cursor: 'pointer',
  };

  return ReactDOM.createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) { (onClose)(e); } }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: 20,
        animation: 'fadeIn 0.15s ease',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={onClose}
        title={t('common.close')}
        aria-label={t('common.close')}
        style={{ ...iconBtnStyle, position: 'absolute', top: 16, right: 16, zIndex: 2 }}
      >
        <X size={18} />
      </button>

      {hasMany && (
        <>
          <button
            type="button"
            onClick={(e) => { stop(e); goPrev(); }}
            title={t('imageViewer.previous')}
            aria-label={t('imageViewer.previous')}
            style={{ ...iconBtnStyle, position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            onClick={(e) => { stop(e); goNext(); }}
            title={t('imageViewer.next')}
            aria-label={t('imageViewer.next')}
            style={{ ...iconBtnStyle, position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
          >
            <ChevronRight size={22} />
          </button>
          <div
            onClick={stop}
            style={{
              position: 'absolute',
              top: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '4px 10px',
              background: 'rgba(0,0,0,0.55)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 12,
              color: '#fff',
              fontSize: 12,
              fontWeight: 500,
              zIndex: 2,
            }}
          >
            {index + 1} / {list.length}
          </div>
        </>
      )}

      <img
        src={current.src}
        alt={current.alt || ''}
        onClick={toggleZoom}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        draggable={false}
        style={{
          maxWidth: zoomed ? 'none' : '90vw',
          maxHeight: zoomed ? 'none' : '90vh',
          width: zoomed ? 'auto' : 'auto',
          height: zoomed ? 'auto' : 'auto',
          objectFit: 'contain',
          borderRadius: 8,
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          cursor: zoomed ? (dragState.current ? 'grabbing' : 'grab') : 'zoom-in',
          userSelect: 'none',
          transform: zoomed ? `translate(${pan.x}px, ${pan.y}px) scale(2)` : 'none',
          transformOrigin: 'center center',
          transition: dragState.current ? 'none' : 'transform 0.18s ease',
          touchAction: zoomed ? 'none' : 'auto',
        }}
      />

      <div
        onClick={stop}
        style={{
          position: 'absolute',
          bottom: 20,
          right: 20,
          display: 'flex',
          gap: 8,
          zIndex: 2,
        }}
      >
        <button
          type="button"
          onClick={toggleZoom}
          title={zoomed ? t('imageViewer.zoomOut') : t('imageViewer.zoomIn')}
          aria-label={zoomed ? t('imageViewer.zoomOut') : t('imageViewer.zoomIn')}
          style={actionBtnStyle}
        >
          {zoomed ? <ZoomOut size={14} /> : <ZoomIn size={14} />}
        </button>
        <a
          href={current.src}
          download
          title={t('common.download')}
          aria-label={t('common.download')}
          style={actionBtnStyle}
        >
          <Download size={14} />
          {t('common.download')}
        </a>
        <a
          href={current.src}
          target="_blank"
          rel="noreferrer"
          title={t('imageViewer.openInNewTab')}
          aria-label={t('imageViewer.openInNewTab')}
          style={actionBtnStyle}
        >
          <ExternalLink size={14} />
          {t('imageViewer.openInNewTab')}
        </a>
        {onSendEdited && (
          <button
            type="button"
            onClick={(e) => { stop(e); setEditing(true); }}
            title={t('imageEditor.edit')}
            aria-label={t('imageEditor.edit')}
            style={actionBtnStyle}
          >
            <Pencil size={14} />
            {t('imageEditor.edit')}
          </button>
        )}
      </div>

      {editing && onSendEdited && (
        <ImageEditor
          src={current.src}
          fileName={current.alt}
          onClose={() => setEditing(false)}
          onSave={async (blob, fileName) => {
            await onSendEdited(blob, fileName);
            setEditing(false);
            onClose?.();
          }}
        />
      )}
    </div>,
    document.body
  );
}
